import React, { useState } from "react";

import { Box } from "@chakra-ui/react";
import { ReactComponent as CopyIcon } from "assets/icons/copy.svg";
import { ComplianceStatus } from "features/business-compliance/selected-business/ComplianceStatus";
import {
  formatDateToDDMMMYYYY,
  handleAmountFormat,
  handleCopyToClipboard,
  handleTruncateAmount,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import { Status } from "ui/status";
import { activeTextToStatus, statusToActiveText } from "ui/status/const";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds } from "ui/table/types";
import { Heading4 } from "ui/typography";

import { businessDetailsTableColumn } from "../../consts";
import { BusinessAccountActions } from "./BusinessAccountActions";
import { BusinessDetailsAccountsTableHeaderCTA } from "./BusinessDetailsAccountsTableHeaderCTA";
import { IBusinessAccountsTableProps } from "./types";

export const BusinessAccountsTable = (props: IBusinessAccountsTableProps) => {
  const { isLoading, businessAccounts, handleGetBusinessAccounts } = props;
  const [selectedBusinessAccountIds, setSelectedBusinessAccountIds] =
    useState<TSelectedTableRowIds>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleCopyAccountNumber = async (accountNumber: string) => {
    await handleCopyToClipboard(
      accountNumber,
      `${accountNumber} Copied Successfully!`
    );
  };

  const formattedData = businessAccounts.map((account) => {
    const {
      id,
      accountName,
      accountNumber,
      balance,
      walletType,
      status,
      accountStatus,
      createdAt,
    } = account;
    const businessActiveStatus = activeTextToStatus[status];

    return {
      "Account Name": (
        <Heading4
          fontSize="14px"
          fontWeight="600"
          lineHeight="20px"
          textColor="#09090B"
        >
          {accountName}
        </Heading4>
      ),
      "Account Number": (
        <Box className="flex items-center gap-3">
          <p className="text-sm text-[#101828]">{accountNumber}</p>
          <CopyIcon
            className="cursor-pointer"
            onClick={() => handleCopyAccountNumber(accountNumber)}
          />
        </Box>
      ),
      Balance: (
        <p className="text-sm text-[#101828]">
          {handleAmountFormat({
            value: (
              handleTruncateAmount(balance) / ONE_NAIRA_IN_KOBO
            ).toString(),
            includeNairaSymbol: true,
            shouldFormatZero: true,
            isFormattedAmtNotPadded: true,
          })}
        </p>
      ),
      "Account Type": (
        <ComplianceStatus
          status={
            walletType.toLowerCase() === "expense" ? "approved" : "pending"
          }
          text={walletType.toLowerCase()}
          customBgColor={`${
            walletType.toLowerCase() === "expense" ? "#EFF8FF" : "#F2F4F7"
          }`}
          customColor={`${
            walletType.toLowerCase() === "expense" ? "#175CD3" : "#344054"
          }`}
          className="w-fit capitalize"
        />
      ),
      Status: (
        <Status
          status={businessActiveStatus}
          text={statusToActiveText[businessActiveStatus]}
          className="w-fit"
        />
      ),
      "Freeze Status":
        accountStatus === "frozen" ? (
          <Status
            status="error"
            text="Frozen"
            className="w-fit"
          />
        ) : (
          <Box className="flex items-center justify-center">-</Box>
        ),
      "Date Created": (
        <p className="text-sm text-[#667085]">
          {formatDateToDDMMMYYYY(createdAt)}
        </p>
      ),
      "": (
        <BusinessAccountActions
          businessAccount={account}
          handleGetBusinessAccounts={handleGetBusinessAccounts}
        />
      ),
      id,
    };
  });

  return (
    <TableWrapper className="!p-0">
      <BusinessDetailsAccountsTableHeaderCTA
        handleGetBusinessAccounts={handleGetBusinessAccounts}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        setIsRefreshing={setIsRefreshing}
      />
      <Table
        columns={businessDetailsTableColumn}
        data={formattedData}
        isLoading={isLoading && !isRefreshing}
        hasCheckbox
        selectedTableRowIds={selectedBusinessAccountIds}
        setSelectedTableRowIds={setSelectedBusinessAccountIds}
      />
    </TableWrapper>
  );
};
